import { createAsyncThunk } from "@reduxjs/toolkit";
import { type Points, Status } from "@store/slices/loyaltyCoreSlice/loyaltyCoreSlice.types";
import type { AppDispatch, RootStoreState } from "@store/types";

import { getConfig, globalEventsHandler } from "@jti/fe-common";
import { getIsEvo, getUser, LoyaltyApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { fetchUserLoyaltyProfile } from "./fetchUserLoyaltyProfile";

type FetchUserPointsPayload = {
  useLocalCache?: boolean;
  dispatchUpdateEvent?: boolean;
  dispatchCodeRegistrationEvent?: boolean;
};

export const fetchUserPoints = createAsyncThunk<
  Points,
  FetchUserPointsPayload,
  {
    state: RootStoreState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>(
  "loyaltyCore/fetchUserPoints",
  async (
    { useLocalCache, dispatchUpdateEvent, dispatchCodeRegistrationEvent },
    { getState, dispatch, rejectWithValue }
  ) => {
    const config = getConfig();
    if (getIsEvo(config.LOYALTY_URL)) {
      await dispatch(
        fetchUserLoyaltyProfile({
          dispatchUpdateEvent,
          dispatchCodeRegistrationEvent,
          skipCache: !useLocalCache,
        })
      );
      return rejectWithValue("Handled by fetchUserLoyaltyProfile");
    }

    const user = getUser();
    const loyaltyApi = LoyaltyApi.init();

    if (user?.uuid && user?.ciam_token) {
      try {
        const response = await loyaltyApi.getPoints(user.uuid, user.ciam_token, useLocalCache);
        const { points, expire_at, points_to_expire } = response;

        if (dispatchUpdateEvent) {
          globalEventsHandler.updateLoyaltyPoints().register(points);
        }

        if (dispatchCodeRegistrationEvent) {
          dispatchTrackingEvent(TrackingEvents.TrackingCodeSubmitSuccess, { user_points_balance: points });
        }

        return { balance: points, expire_at, points_to_expire, status: Status.COMPLETED };
      } catch (error) {
        console.error(error);
        throw new Error("Error while fetching user points");
      }
    }

    const points = getState().loyaltyCore.points;

    return { ...points, status: Status.FAILED };
  }
);
